import { ChangeEvent, useContext, useEffect, useState } from 'react';
import FormStructureContext from '../context/FormStructureContext';
import InputContext from '../context/InputContext';
import { SelectProps, SelectValue } from '../schema/interface';
import { SET_FIELD } from './reducer';
import useAlgorithm from './useAlgorithm';

const useMultiSelect = ({
    name,
    items,
    separator,
    value,
    min,
    max,
    restricted,
    required,
    handleChangeVisible,
    formValues
}: SelectProps) => {
    const { dispatch } = useContext(InputContext);

    const [errorOtherOption, setErrorOtherOption] = useState(false);
    const [options, setOptions] = useState<string[][]>([]);
    const [selectedOptions, setSelectedOptions] = useState<string[][]>();
    const [otherOptionValue, setOtherOptionValue] = useState<string>('');
    const { fetchedFormStructure: formStructure } = useContext(FormStructureContext);
    const { handleAlgorithmCode } = useAlgorithm({
        formStructure,
        formValues,
        handleChangeVisible
    });

    useEffect(() => {
        if (items instanceof Array) {
            setOptions(items);
        } else {
            const listOfItems = items ? items.split(separator) : [];
            setOptions(listOfItems.map((val) => [val, val]));
        }
    }, [items]);
    useEffect(() => {
        if (restricted === false && value) {
            if (value instanceof Array) {
                const found = value.find((val) => !options?.find((option) => option[0] === val));
                if (found) {
                    setOtherOptionValue(found);
                } else {
                    setOtherOptionValue('');
                }
            } else if ((value as SelectValue)?.others?.length) {
                setOtherOptionValue((value as SelectValue).others?.toString());
            } else {
                setOtherOptionValue('');
            }
        }
    }, [value, options, restricted]);
    useEffect(() => {
        if ((value as SelectValue)?.choices?.length) {
            const mappedList = options.filter((val) =>
                typeof (value as SelectValue).choices[0] === 'string'
                    ? (value as SelectValue).choices?.includes(val[0])
                    : (value as SelectValue).choices?.find((choice) => choice?.[0] === val[0])
            );
            setSelectedOptions(mappedList);
        } else if (value) {
            let selectedList;
            if (typeof value === 'string') {
                selectedList = options.filter((val) => value === val[0]);
                setSelectedOptions(selectedList);
            } else {
                selectedList = options.filter((val) =>
                    (value as Array<string>).find?.((select) => select === val[0])
                );
                setSelectedOptions(selectedList);
            }
        }
    }, [value, options]);

    const handleSelect = (newSelectedValues: string[] = []) => {
        const otherValue = (value as SelectValue)?.others;
        dispatch({
            type: SET_FIELD,
            name,
            value: {
                choices: newSelectedValues,
                others: otherValue || []
            }
        });
    };
    const handleToggleOption = (value: string) => {
        const selected = value;
        let newList: string[];
        if (selectedOptions?.length) {
            if (selectedOptions.find((val) => val[0] === selected)) {
                newList = selectedOptions
                    .filter((val: string[]) => val[0] !== selected)
                    .map?.((val) => val[0]);
            } else {
                newList = [...selectedOptions.map((val) => val[0]), selected];
            }
        } else {
            newList = [selected];
        }
        handleSelect(newList);
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const otherValue = e.target.value;
        const choicesValue = (value as SelectValue).choices;

        dispatch({
            type: SET_FIELD,
            name,
            value: {
                choices: choicesValue || [],
                others: otherValue ? [otherValue] : []
            }
        });
        setErrorOtherOption(false);
    };
    const validatedOtherOption = () => {
        if (value) {
            handleAlgorithmCode();
        }
        const selectedOptions = options.filter((val) =>
            (value as SelectValue)?.choices?.includes(val[0])
        );
        const found = selectedOptions.find(
            (val) => val[1] === (value as SelectValue)?.others?.toString()
        );
        if (found) {
            setErrorOtherOption(true);
        }
    };
    const handleBlur = () => {
        if (value) {
            handleAlgorithmCode();
        }
    };
    return {
        options,
        handleSelect,
        handleInputChange,
        errorOtherOption,
        validatedOtherOption,
        value,
        min,
        max,
        selectedOptions,
        otherOptionValue,
        handleToggleOption,
        name,
        required,
        handleBlur
    };
};
export default useMultiSelect;
